import { useFormContext, useWatch } from 'react-hook-form';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';

import AdvanceFileInput from 'components/field/AdvanceFileInput';
import Badge from 'components/field/Badge';
import ImageInput from 'components/field/ImageInput';
import StringInput from 'components/field/StringInput';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Authorization from 'components/global/Authorization';
import Icon from 'components/global/Icon';
import Radio from 'components/global/Radio';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';

import { ACCOUNT_TYPES } from 'utils/constants';

import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

import * as S from './GeneralSettings.styled';

const defaultColSpan = {
	lg: 8,
	xl: 7
};

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const InvoiceTypeFormForSettings = ({ chargeAccount }) => {
	const {
		setValue,
		control,
		watch,
		formState: { isSubmitting, isLoading: isFormLoading, errors, defaultValues }
	} = useFormContext();

	const isLoading = isFormLoading || isSubmitting;

	const isAuthorized = useAccessControl();
	const {
		VATNumber,
		CRNumber,
		CRDocumentURL,
		idNumber,
		fullName,
		taxOffice,
		companyName,
		unifiedNumber,
		civilID,
		freelancerNumber,
		nationalId
	} = useSelector((state) => state?.user?.generalSettings);

	const invoiceType = useWatch({
		control,
		name: 'invoiceType'
	});

	const CRDocStatus = useWatch({
		control,
		name: 'CRDocStatus'
	});

	const handleAccountTypeClick = (type) => {
		setValue('invoiceType', type, validateOptions);
		setValue('notVATRegistered', type !== ACCOUNT_TYPES.COMPANY, validateOptions);

		if (type === ACCOUNT_TYPES.COMPANY) {
			// Company specific fields
			setValue('fullName', fullName || null, validateOptions);
			setValue('idNumber', idNumber || null, validateOptions);
			setValue('unifiedNumber', unifiedNumber || null, validateOptions);
			setValue('nationalId', null, validateOptions);
			setValue('freelancerNumber', null, validateOptions);
		} else if (type === ACCOUNT_TYPES.FREELANCER) {
			setValue('companyName', companyName || null, validateOptions);
			setValue('VATNumber', VATNumber || null, validateOptions);
			setValue('taxOffice', taxOffice || null, validateOptions);
			setValue('CRNumber', CRNumber || null, validateOptions);
			setValue('CRDocumentURL', CRDocumentURL || null, validateOptions);
			setValue('nationalId', nationalId, validateOptions);
			setValue('freelancerNumber', freelancerNumber, validateOptions);
			setValue('unifiedNumber', null, validateOptions);
		} else {
			setValue('companyName', companyName || null, validateOptions);
			setValue('VATNumber', VATNumber || null, validateOptions);
			setValue('taxOffice', taxOffice || null, validateOptions);
			setValue('CRNumber', CRNumber || null, validateOptions);
			setValue('civilID', civilID ? { path: civilID, ...defaultValues?.civilID } : null, validateOptions);
			setValue('CRDocumentURL', CRDocumentURL || null, validateOptions);
			setValue('unifiedNumber', null, validateOptions);
			setValue('nationalId', null, validateOptions);
			setValue('freelancerNumber', null, validateOptions);
		}
	};

	const radioItems = [
		{
			type: ACCOUNT_TYPES.PERSONAL,
			label: 'personal'
		},
		{
			type: ACCOUNT_TYPES.FREELANCER,
			label: 'freelancer',
			authorization: ['/company-settings/CommonInvoiceType']
		},
		{
			type: ACCOUNT_TYPES.COMPANY,
			label: 'company'
		}
	];

	return (
		<FormItem
			colSpanChild={defaultColSpan}
			label="invoiceType"
			isLoading={isLoading}
			className={'base-form-item px-0'}
			required
			toolTip="invoiceType.description">
			<S.RadioContainer>
				{radioItems.map((item) => (
					<Authorization path={item.authorization} key={item.type}>
						<S.RadioItem onClick={() => handleAccountTypeClick(item.type)}>
							<Radio readOnly checked={invoiceType === item.type} error={!!errors?.invoiceType} />
							<S.NewAccountTypeArea>
								<Typography fontWeight="500">{item.label}</Typography>
								{item.isNew && <Badge variant="pill" size="small" color="red" text="new" />}
							</S.NewAccountTypeArea>
						</S.RadioItem>
					</Authorization>
				))}
			</S.RadioContainer>
			{invoiceType === ACCOUNT_TYPES.PERSONAL && (
				<S.InvoiceTypeItemContainer noPadding>
					<FormItem
						roleName="/company-settings/TRInvoiceType"
						col={6}
						label="fullName"
						isLoading={isLoading}
						required>
						<StringInput name="fullName" />
					</FormItem>
					<FormItem
						roleName="/company-settings/TRInvoiceType"
						col={6}
						label="idNumber"
						isLoading={isLoading}
						required>
						<StringInput name="idNumber" maxlength={11} allowOnlyNumbers />
					</FormItem>
				</S.InvoiceTypeItemContainer>
			)}

			<Authorization path={['/company-settings/CommonInvoiceType']}>
				{!chargeAccount && invoiceType === ACCOUNT_TYPES.FREELANCER && (
					<S.InvoiceTypeItemContainer fullWitdhItems noPadding gap="1.5rem">
						<FormItem
							roleName="/company-settings/KSAInvoiceType"
							col={6}
							label="freelancerNationalId"
							isLoading={isLoading}
							allowOnlyNumbers>
							<StringInput name="nationalId" allowOnlyNumbers maxlength={10} />
						</FormItem>
						<FormItem
							roleName="/company-settings/KSAInvoiceType"
							col={6}
							label="freelancerNumber"
							isLoading={isLoading}>
							<StringInput name="freelancerNumber" />
						</FormItem>
						<FormItem col={6} label="freelanceDocument" isLoading={isLoading}>
							<AdvanceFileInput
								name="freelanceDocument"
								style={{ width: '100%' }}
								btnStyle={{ width: '100%' }}
								acceptedFileType={'*'}
								setValue={(value) => {
									setValue('freelanceDocument', value?.path ? value : null, validateOptions);
								}}
								value={
									watch('freelanceDocument') || {
										name: null,
										path: null
									}
								}
								maxFileSize={1000000}
							/>
						</FormItem>
						<FormItem col={6} label="civilIDFrontSide" isLoading={isLoading}>
							<AdvanceFileInput
								name="civilID"
								style={{ width: '100%' }}
								btnStyle={{ width: '100%' }}
								acceptedFileType={'*'}
								setValue={(value) => {
									setValue('civilID', value?.path ? value : null, validateOptions);
								}}
								value={
									watch('civilID') || {
										name: null,
										path: null
									}
								}
								maxFileSize={1000000}
							/>
						</FormItem>
					</S.InvoiceTypeItemContainer>
				)}
			</Authorization>

			{invoiceType === ACCOUNT_TYPES.COMPANY && (
				<S.InvoiceTypeItemContainer fullWitdhItems noPadding>
					<FormItem
						roleName="/company-settings/CommonInvoiceType"
						col={6}
						label="crNumber"
						isLoading={isLoading}
						required>
						<StringInput
							name="CRNumber"
							allowOnlyNumbers
							maxlength={isAuthorized('/company-settings/KSAInvoiceType') ? 10 : 9}
						/>
					</FormItem>

					<FormItem
						roleName="/company-settings/KSAInvoiceType"
						col={6}
						label="unifiedNumber"
						isLoading={isLoading}>
						<StringInput name="unifiedNumber" allowOnlyNumbers maxlength={10} />
					</FormItem>

					<FormItem
						roleName="/company-settings/TRInvoiceType"
						col={6}
						label="companyName"
						isLoading={isLoading}
						required>
						<StringInput name="companyName" />
					</FormItem>

					<FormItem
						roleName="/company-settings/TRInvoiceType"
						col={6}
						label="taxOffice"
						isLoading={isLoading}
						required>
						<StringInput name="taxOffice" />
					</FormItem>

					{isAuthorized('/company-settings/CommonInvoiceType') && (
						<FormItem label="isVATRegistered">
							<SwitchInput
								value={watch('isVatNumberRequired')}
								setValue={(e) => {
									setValue('isVatNumberRequired', e.target.checked, validateOptions);
								}}
							/>
						</FormItem>
					)}

					<FormItem
						col={6}
						label="VATNumber"
						isLoading={isLoading}
						required={isAuthorized('/company-settings/TRInvoiceType') || watch('isVatNumberRequired')}>
						<StringInput
							name="VATNumber"
							allowOnlyNumbers
							maxlength={isAuthorized('/company-settings/TRInvoiceType') ? 10 : 15}
						/>
					</FormItem>
					{!chargeAccount && (
						<>
							<FormItem roleName="/company-settings/CommonInvoiceType" col={6}>
								<Alert variant="warning" content="companySettings.VATNumberWarning" showIcon />
							</FormItem>
							<FormItem
								roleName="/company-settings/CommonInvoiceType"
								label={'CRDocumentLabel'}
								isLoading={isLoading}>
								{!['VERIFIED', 'WAITING_VERIFICATION'].includes(CRDocStatus) && (
									<>
										<ImageInput
											value={watch('CRDocumentURL')}
											placeholder="selectCRDocument"
											name="CRDocumentURL"
											setValue={(value) => {
												setValue('CRDocumentURL', value, validateOptions);
											}}
										/>
										{!watch('CRDocumentURL') && (
											<S.FlexRow color={'var(--red)'}>
												<Icon icon={faExclamationCircle} />
												<Typography component="p" fullWidth noWrap>
													<Trans i18nKey="companySettings.CRDocumentWarning" />
												</Typography>
											</S.FlexRow>
										)}
									</>
								)}
								{CRDocStatus === 'WAITING_VERIFICATION' && (
									<Alert
										variant="warning"
										content="companySettings.CRDocumentWaitingVerification"
										showIcon
									/>
								)}
								{CRDocStatus === 'VERIFIED' && (
									<Alert variant="success" content="companySettings.CRDocumentVerified" showIcon />
								)}
							</FormItem>
							<FormItem
								roleName="/company-settings/KSAInvoiceType"
								col={6}
								label="civilIDOrIqama"
								isLoading={isLoading}>
								<AdvanceFileInput
									name="civilID"
									style={{ width: '100%' }}
									btnStyle={{ width: '100%' }}
									acceptedFileType={'*'}
									setValue={(value) => {
										setValue('civilID', value?.path ? value : null, validateOptions);
									}}
									value={
										watch('civilID') || {
											name: null,
											path: null
										}
									}
									maxFileSize={1000000}
								/>
							</FormItem>
						</>
					)}
				</S.InvoiceTypeItemContainer>
			)}
		</FormItem>
	);
};

export default InvoiceTypeFormForSettings;
